// Market-closed hours on the price chart (same file in the TetherChand app).
// The history API sends `market_closed_ranges` (Unix seconds); the chart squeezes each one into a narrow
// «closed» band so a night of no trading is not drawn as a long flat line. The hours come from the API,
// never from here, so a change of the rule needs no release.

export interface ClosedRange {
    from: number;
    to: number;
}

/** Ranges whose closed hours are squeezed. From 7d up the API has one point per 12h or more, and the
 *  03:30 point always falls in the closed hours, so squeezing there would only add stray bands. */
export const CLOSED_RANGE_KEYS = ['1h', '3h', '12h', '24h'];

/** Width of one squeezed closed band, px */
export const CLOSED_GAP_PX = 14;

/** Valid ranges from an API answer; anything malformed is ignored */
export function readClosedRanges(raw: unknown): ClosedRange[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((r) => ({ from: Number(r?.from), to: Number(r?.to) }))
        .filter((r) => r.from > 0 && r.to > r.from);
}

export interface TimeAxis {
    /** Time → x (px) */
    x: (t: number) => number;
    /** Squeezed closed bands, in px and in time */
    gaps: { from: number; to: number; x0: number; x1: number }[];
    /** True when `t` falls inside a squeezed band */
    inGap: (t: number) => boolean;
}

/**
 * Time axis from t0 to t1 over `width` px. Closed ranges (at least 1 hour long inside the window) become
 * narrow bands; the open hours share the rest. Falls back to a plain linear axis when there is nothing to
 * squeeze or the window is (almost) all closed.
 */
export function buildTimeAxis(
    t0: number,
    t1: number,
    width: number,
    ranges: readonly ClosedRange[] = [],
    gapPx = CLOSED_GAP_PX,
): TimeAxis {
    const span = t1 - t0 || 1;
    const linear: TimeAxis = { x: (t) => ((t - t0) / span) * width, gaps: [], inGap: () => false };

    // Clip to the window, keep ≥ 1h, merge overlaps
    const gaps: ClosedRange[] = [];
    for (const r of [...ranges].sort((a, b) => a.from - b.from)) {
        const from = Math.max(r.from, t0);
        const to = Math.min(r.to, t1);
        if (to - from < 3600) continue;
        const prev = gaps[gaps.length - 1];
        if (prev && from <= prev.to) prev.to = Math.max(prev.to, to);
        else gaps.push({ from, to });
    }
    if (!gaps.length) return linear;

    const closed = gaps.reduce((s, g) => s + (g.to - g.from), 0);
    const open = span - closed;
    const usable = width - gaps.length * gapPx;
    if (open < span * 0.1 || usable < width * 0.5) return linear;
    const k = usable / open;

    const x = (t: number) => {
        let px = 0;
        let prev = t0;
        for (const g of gaps) {
            if (t <= g.from) return px + (t - prev) * k;
            px += (g.from - prev) * k;
            if (t < g.to) return px + ((t - g.from) / (g.to - g.from)) * gapPx;
            px += gapPx;
            prev = g.to;
        }
        return px + (t - prev) * k;
    };

    return {
        x,
        gaps: gaps.map((g) => ({ ...g, x0: x(g.from), x1: x(g.to) })),
        inGap: (t) => gaps.some((g) => t > g.from && t < g.to),
    };
}

/** «۲۳:۳۰ تا ۱۱:۳۰» for the caption under the chart. Pass the API's ranges, not ones clipped to the chart,
 *  so the real hours show; the longest one is used. */
export function closedHoursLabel(gaps: readonly { from: number; to: number }[]): string {
    if (!gaps.length) return '';
    const g = gaps.reduce((a, b) => (b.to - b.from > a.to - a.from ? b : a));
    const hm = (t: number) =>
        new Date(t * 1000).toLocaleTimeString('fa-IR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    return `${hm(g.from)} تا ${hm(g.to)}`;
}
